import { type HighlightsRenderer } from '@databases/highlights';

// Load the drawable presence layer's styles as a side-effect when this module
// is imported. The renderer is the sole consumer of these rules, so they live
// with it instead of in `@databases/highlights`.
import '../styles/index.ts';
import { type PresenceLayerGroupSnapshot } from '../presence-state.ts';
import {
  PRESENCE_BOX_CLASS,
  PRESENCE_BOX_HOVERED_CLASS,
  PRESENCE_NAMEPLATE_CLASS
} from '../styles/constants.ts';

import { buildNameplateText } from './build-nameplate-text.ts';
import { PRESENCE_LAYER_Z_INDEX } from './constants.ts';
import { type FormatNameplateOverflow, type PresenceLayerRendererDeps } from './types.ts';

interface Placement {
  id: string;
  row: number;
  mode: 'cell' | 'column';
  /**
   * Inclusive column span. Cell presence spans a single column; row presence
   * spans the whole data area (col 0..last) so its box sits inside the grid's
   * sticky entry-number gutter instead of being clipped underneath it.
   */
  anchorCol: number;
  leadCol: number;
}

/**
 * Resolves a presence group to its grid placement, or `null` when it should not
 * be drawn by this layer (an invalid grouping, or a field/entry not currently
 * visible). Cell presence needs both a visible field and entry; row presence
 * needs only a visible entry and at least one visible column to span; column
 * presence needs only a visible field.
 */
function resolvePlacement(
  group: PresenceLayerGroupSnapshot,
  fieldIndexById: ReadonlyMap<string, number>,
  entryIndexById: ReadonlyMap<string, number>,
  lastCol: number
): Placement | null {
  if (group.entryId === null) {
    if (group.fieldId === null) {
      return null;
    }

    const col = fieldIndexById.get(group.fieldId);

    return col === undefined
      ? null
      : {
          id: `column:${group.fieldId}`,
          row: 0,
          mode: 'column',
          anchorCol: col,
          leadCol: col
        };
  }

  const row = entryIndexById.get(group.entryId);

  if (row === undefined) {
    return null;
  }

  if (group.fieldId === null) {
    if (lastCol < 0) {
      return null;
    }

    return {
      id: `row:${group.entryId}`,
      row,
      mode: 'cell',
      anchorCol: 0,
      leadCol: lastCol
    };
  }

  const col = fieldIndexById.get(group.fieldId);

  if (col === undefined) {
    return null;
  }

  return {
    id: `cell:${group.fieldId}:${group.entryId}`,
    row,
    mode: 'cell',
    anchorCol: col,
    leadCol: col
  };
}

/**
 * Appends the nameplate child (collaborator names) to a presence box element,
 * unless there are no names to show.
 */
function appendNameplate(
  el: HTMLElement,
  group: PresenceLayerGroupSnapshot,
  formatOverflow: FormatNameplateOverflow
): () => void {
  const names = group.participants
    .map((participant) => participant.displayName)
    .filter((name): name is string => name !== undefined);

  const text = buildNameplateText(names, formatOverflow);

  if (text === '') {
    return () => {};
  }

  const nameplate = document.createElement('div');
  nameplate.className = PRESENCE_NAMEPLATE_CLASS;
  nameplate.textContent = text;
  el.appendChild(nameplate);

  return () => nameplate.remove();
}

/**
 * Toggles the hovered modifier on each box based on whether the pointer is
 * inside its on-screen rect. The box keeps `pointer-events: none` so the grid
 * stays clickable (selection hit-tests via `elementFromPoint`), which means
 * hover can't be a CSS `:hover`; we hit-test in JS instead.
 */
function applyHover(
  boxes: HTMLElement[],
  pointer: { x: number; y: number } | null
): void {
  for (const box of boxes) {
    const rect = box.getBoundingClientRect();
    const hovered =
      pointer !== null &&
      pointer.x >= rect.left &&
      pointer.x <= rect.right &&
      pointer.y >= rect.top &&
      pointer.y <= rect.bottom;

    box.classList.toggle(PRESENCE_BOX_HOVERED_CLASS, hovered);
  }
}

/**
 * The drawable presence layer. This module owns one `HighlightsController`
 * layer and draws all remote collaborator boxes (cells, rows, and columns)
 * plus their nameplates as plain DOM.
 *
 * It is driven by the injected `presence` store: `startListening` subscribes
 * and redraws the whole layer on every presence change (clear + add), coalescing
 * bursts into one animation frame. Placement changes (view configuration or
 * visible field/entry ordering) use the same scheduler. It is event-driven, not
 * a polling loop. The controller's own `ResizeObserver` keeps boxes aligned on
 * column resize, and the overlay scrolls natively with the grid, so no scroll
 * handling is required. Nameplates are hidden until the pointer hovers a box;
 * hover is tracked from `pointermove` and reflected on the box's class.
 */
export function createPresenceLayerRenderer({
  controller,
  presence,
  adapter,
  subscribeToPlacementChanges,
  subscribeToPresentationChanges = () => () => {},
  isPresenceVisible = () => true,
  formatOverflow
}: PresenceLayerRendererDeps): { startListening(): void; stopListening(): void } {
  let layer: HighlightsRenderer | null = null;
  let unsubscribePresence: (() => void) | null = null;
  let unsubscribePlacementChanges: (() => void) | null = null;
  let unsubscribePresentationChanges: (() => void) | null = null;
  let placementDirty = true;
  let fieldIndexById = new Map<string, number>();
  let entryIndexById = new Map<string, number>();
  let lastCol = -1;
  let rafId: number | null = null;
  let hoverRafId: number | null = null;
  let pointer: { x: number; y: number } | null = null;
  let boxes: HTMLElement[] = [];

  function draw(): void {
    // Keep logical ranges before lazy attachment; the controller mounts and
    // redraws them when the renderer publishes its committed geometry.
    if (!layer) {
      layer = controller.createLayer();
    }

    layer.clear();
    boxes = [];
    const groups = presence.getGroups();
    if (groups.length === 0) {
      return;
    }
    if (placementDirty) {
      const fieldIds = adapter.getVisibleFieldIds();
      const entryIds = adapter.getVisibleEntryIds();
      fieldIndexById = new Map(fieldIds.map((id, index) => [id, index]));
      entryIndexById = new Map(entryIds.map((id, index) => [id, index]));
      lastCol = fieldIds.length - 1;
      placementDirty = false;
    }

    for (const group of groups) {
      const placement = resolvePlacement(group, fieldIndexById, entryIndexById, lastCol);

      if (
        !placement ||
        !isPresenceVisible(group, {
          rowIndex: placement.row,
          columnIndex: placement.anchorCol
        })
      ) {
        continue;
      }

      // Palette hue lives at index 3 of `color.background.accent.<hue>.bolder`.
      const hue = group.participants[0].colorToken.split('.')[3];

      const handle = layer.add({
        range: {
          id: placement.id,
          anchor: { row: placement.row, col: placement.anchorCol },
          lead: { row: placement.row, col: placement.leadCol }
        },
        mode: placement.mode,
        zIndex: PRESENCE_LAYER_Z_INDEX,
        className: `${PRESENCE_BOX_CLASS} ${PRESENCE_BOX_CLASS}--${hue}`
      });

      handle.bindElements(({ element, role }) => {
        boxes.push(element);
        applyHover([element], pointer);
        const removeNameplate =
          role === 'primary' ? appendNameplate(element, group, formatOverflow) : () => {};

        return () => {
          removeNameplate();
          boxes = boxes.filter((box) => box !== element);
        };
      });
    }

    applyHover(boxes, pointer);
  }

  function scheduleDraw(): void {
    if (rafId !== null) {
      return;
    }

    rafId = requestAnimationFrame(() => {
      rafId = null;
      draw();
    });
  }

  function scheduleHover(): void {
    if (hoverRafId !== null) {
      return;
    }

    hoverRafId = requestAnimationFrame(() => {
      hoverRafId = null;
      applyHover(boxes, pointer);
    });
  }

  function handlePointerMove(event: PointerEvent): void {
    pointer = { x: event.clientX, y: event.clientY };
    scheduleHover();
  }

  return {
    startListening() {
      unsubscribePresence = presence.subscribe(scheduleDraw);
      unsubscribePlacementChanges = subscribeToPlacementChanges(() => {
        placementDirty = true;
        scheduleDraw();
      });
      unsubscribePresentationChanges = subscribeToPresentationChanges(scheduleDraw);
      document.addEventListener('pointermove', handlePointerMove);
      draw();
    },

    stopListening() {
      unsubscribePresence?.();
      unsubscribePresence = null;
      unsubscribePlacementChanges?.();
      unsubscribePlacementChanges = null;
      unsubscribePresentationChanges?.();
      unsubscribePresentationChanges = null;
      placementDirty = true;
      fieldIndexById.clear();
      entryIndexById.clear();

      document.removeEventListener('pointermove', handlePointerMove);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (hoverRafId !== null) {
        cancelAnimationFrame(hoverRafId);
        hoverRafId = null;
      }

      pointer = null;
      boxes = [];

      if (layer) {
        layer.dispose();
        layer = null;
      }
    }
  };
}
